import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, ErrorMessage } from 'formik';
import Api from '../../Api/Api';
import ServerValidatedField from './validateField/ServerValidatedField';
import {
  PageWrapper,
  Title,
  Subtitle,
  Label,
  SubLabel,
  SubLabelIdProvider,
  CheckLabel,
  CheckField,
  Flex,
  Input,
  StyledInlineErrorMessage,
  StyledInlineSuccessMessage,
  Submit,
  SelectProvider,
  LabelText,
  CodeWrapper,
  ImageContainer,
  Img,
  Link,
  LinkSubtitle,
  GlobalStyle,
  Dropdown,
  Section
} from '../../styles';
import { TermsAndConditions } from './termsAndConditions/TermsAndConditions';
import { checkList, checkStoreName } from '../../Api/Api';

const handleIdentityProviderClick = (type, action) => {
    if (type === 'ADFS') {
    action('identityProviderType', 'ADFS', true);
  } else {
    action('identityProviderType', 'AzureAD', true);
  }
}

const trimValuesBeforeSubmit = (values) => {
  const trimmedValues = {
    fullname: values.fullname.trim(),
    companyname: values.companyname.trim(),
    storename: values.storename.trim(),
    email: values.email.trim(),
    adfsHostname: values.adfsHostname.trim(),
    adfsThumbprint: values.adfsThumbprint.trim(),
    tenantId: values.tenantId.trim(),
    acceptFollow: values.acceptFollow,
    acceptToS: values.acceptToS,
    identityProviderType: values.identityProviderType
  }
  
  return trimmedValues;
}

function NewStoreForm(props) {
  const [formValues, setFormValues] = useState();
  const [serverErrors, setServerErrors] = useState();
  const api = new Api();

  return (
    <PageWrapper>
      <ImageContainer>
        <Img src='https://zervicepoint.com/wp-content/uploads/2019/11/zp_logo_name.svg' />
      </ImageContainer>
      <Title>
        Create business automation and self-service for your work place
      </Title>
      <Subtitle>
        Sign up for Zervicepoint Cloud to build you own company portal
      </Subtitle>

      <Formik
        initialValues={{
          fullname: "",
          companyname: "",
          email: "",
          storename: "",
          acceptToS: false,
          acceptFollow: false,
          identityProviderType: "",
          adfsHostname: "",
          adfsThumbprint: "",
          tenantId: ""
        }}

      // initialValues={{ identityProviderType: '', adfsHostname: false, tenantID: false }}
        validationSchema={Yup.object().shape({
          fullname: Yup.string()
            .min(2, "Your name is too short")
            .required("Please enter your full name")
            .max(255, "The name is too long"),
          companyname: Yup.string()
            .required("Please enter the name of your company or organization")
            .max(255, "The company name is too long"),
          email: Yup.string()
            .email("Your email doesn't look right")
            .required("Please enter your email")
            .max(255, "The email address is too long"),
          storename: Yup.string()
            .min(2, "The store name is way too short")
            .max(50, "The storename is too long")
            .required("Please enter a store name")
            .matches(/^[A-Za-z]+$/, "Numbers are not allowed."),
          acceptToS: Yup.bool()
            .oneOf([true]),
          identityProviderType: Yup.string()
            .oneOf(["ADFS", "AzureAD"])
            .required("Please choose one of the alternatives"),
          adfsHostname: Yup.string()
            .min(4, "Your host name is to short sts.example.com")
            .when('identityProviderType', {
              is: 'ADFS',
              then: Yup.string().required("You need to provide an ADFS host name")
            .max(255, "ADFS host name is too long"),
            }),
            
          adfsThumbprint: Yup.string()
            .when('identityProviderType', {
              is: 'ADFS',
              then: Yup.string().required("You need to provide thumbprint for ADFS")  
            .max(255, "The ADFS thumbprint is too long"),
            }),
          tenantId: Yup.string()
            .when('identityProviderType', {
              is: 'AzureAD',
              then: Yup.string().required("You need to provide an Azure AD tenant ID")
            .max(255, "The tenant id is too long"),
            }),
        })}
        onSubmit={(values, actions) => {
          const trimmedValues = trimValuesBeforeSubmit(values);
          setFormValues(trimmedValues);
          const timeOut = setTimeout(() => {
            api.createSubmitRequest(trimmedValues)
              .then(() => {
                props.onSubmit(trimmedValues);
              })
              .catch(error => {
                console.log(error.message);
                setServerErrors(error.message);
              })
              .finally(() => {
                actions.setSubmitting(false);
              })
            clearTimeout(timeOut);
          }, 1000);
        }}
      >
        {({
          values,
          type,
          selected,
          errors,
          touched,
          handleSubmit,
          isSubmitting,
          validating,
          isValid,
          setFieldValue,
          identityProviderType
        }) => {

          return (
            <React.Fragment>
              <Form name="contact" method="post" onSubmit={handleSubmit}>
                <Label htmlFor="fullname">
                  Full name
                  <Input
                    id="fullname"
                    type="text"
                    name="fullname"
                    autoCorrect="off"
                    autoComplete="name"
                    placeholder="Enter your name"
                    selected={touched.fullname && !errors.fullname}
                    error={touched.fullname && errors.fullname}
                  />
                </Label>
                {errors.fullname && touched.fullname && (
                  <StyledInlineErrorMessage>
                    {errors.fullname}
                  </StyledInlineErrorMessage>
                )}
                <Label htmlFor="companyname">
                  Company name
                  <Input
                    type="text"
                    name="companyname"
                    autoCorrect="off"
                    autoComplete="name"
                    placeholder="Enter your company name"
                    selected={touched.companyname && !errors.companyname}
                    error={touched.companyname && errors.companyname}
                  />
                </Label>
                {errors.companyname && touched.companyname && (
                  <StyledInlineErrorMessage>
                    {errors.companyname}
                  </StyledInlineErrorMessage>
                )}
                <Label htmlFor="email">
                  Email
                  <ServerValidatedField
                    type="email"
                    name="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    autoComplete="email"
                    placeholder="Enter your email"
                    selected={touched.email && !errors.email}
                    error={touched.email && errors.email}
                  />
                </Label>
                <ErrorMessage name="email">
                  {(msg) => (
                    <StyledInlineErrorMessage>{msg}</StyledInlineErrorMessage>
                  )}
                </ErrorMessage>
                <Label htmlFor="storename">
                  Store name
                  <SubLabel htmlFor="storename">
                  The store name will be reflected in the portal’s URL.
                  </SubLabel>
                  <ServerValidatedField
                    type="text"
                    name="storename"
                    autoCorrect="off"
                    autoComplete="name"
                    placeholder="Enter the name of your store"
                    selected={touched.storename && !errors.storename}
                    error={touched.storename && errors.storename}
                  />
                </Label>
                {errors.storename && touched.storename && (
                  <StyledInlineErrorMessage>
                    {errors.storename}
                  </StyledInlineErrorMessage>
                )}
                <Section>
                <Label htmlFor="identityProviderType">
                  Select an identity provider
                  <SubLabel htmlFor="identityProviderType">
                  </SubLabel>
                  </Label>
                  <SelectProvider
                    onClick={(event) => handleIdentityProviderClick('ADFS', setFieldValue)}
                    style={ values.identityProviderType === "ADFS" ? { backgroundColor: '#E7F4F7', borderColor: '#979797', color: '#313641' } : null }
                    type="button"
                  >
                    ADFS
                  </SelectProvider>
                  <SelectProvider
                    onClick={(event) => handleIdentityProviderClick('AzureAD', setFieldValue)}
                    style={ values.identityProviderType === "AzureAD" ? { backgroundColor: '#E7F4F7', borderColor: '#979797', color: '#313641' } : null }
                    type="button"
                  >
                    Azure AD
                  </SelectProvider>
                  </Section>
                {errors.identityProviderType && touched.identityProviderType && (
                  <StyledInlineErrorMessage>
                    {errors.identityProviderType}
                  </StyledInlineErrorMessage>
                )}
                {values.identityProviderType === "ADFS" ?
                    <Section>
                      <Label htmlFor="adfsHostname">
                        ADFS host name
                        <SubLabelIdProvider htmlFor="adfsHostname">
                        <LinkSubtitle href="https://docs.zervicepoint.com/onboarding/cloud-onboarding-signup/#adfs" target="_blank">
                        If you plan to use ADFS you will need to create a relying party in your ADFS environment.</LinkSubtitle>
                        </SubLabelIdProvider>
                        <Input
                          type="text"
                          name="adfsHostname"
                          autoCapitalize="off"
                          autoCorrect="off"
                          autoComplete="adfsHostname"
                          placeholder="Enter your ADFS host name"
                          selected={touched.adfsHostname && !errors.adfsHostname}
                          error={touched.adfsHostname && errors.adfsHostname}
                        />
                      </Label>
                      {errors.adfsHostname && touched.adfsHostname && (
                        <ErrorMessage name="adfsHostname">
                        {(msg) => (
                          <StyledInlineErrorMessage>{msg}</StyledInlineErrorMessage>
                        )}
                      </ErrorMessage>
                      )}
                      <Label htmlFor="adfsThumbprint">
                        ADFS token signing thumbprint
                        <Input
                          type="text"
                          name="adfsThumbprint"
                          autoCapitalize="off"
                          autoCorrect="off"
                          autoComplete="adfsThumbprint"
                          placeholder="Enter your ADFS thumbprint"
                          selected={touched.adfsThumbprint && !errors.adfsThumbprint}
                          error={touched.adfsThumbprint && errors.adfsThumbprint}
                        />
                      </Label>
                      {errors.adfsThumbprint && touched.adfsThumbprint && (
                        <ErrorMessage name="adfsThumbprint">
                        {(msg) => (
                          <StyledInlineErrorMessage>{msg}</StyledInlineErrorMessage>
                        )}
                      </ErrorMessage>
                      )}
                    </Section>
                    :  values.identityProviderType === "AzureAD" ?
                    <Section>
                      <Label htmlFor="tenantId">
                        Azure tenant ID
                        <SubLabelIdProvider htmlFor="tenantId">
                        This is a GUID representing the id of the Azure AD tenant where your users authenticate from. You can find this value in Azure AD settings in the Azure portal.
                        </SubLabelIdProvider>
                        <Input
                          type="text"
                          name="tenantId"
                          autoCapitalize="off"
                          autoCorrect="off"
                          autoComplete="tenantId"
                          placeholder="Enter your Azure tenant ID"
                          selected={touched.tenantId && !errors.tenantId}
                          error={touched.tenantId && errors.tenantId}
                        />
                      </Label>
                      {errors.tenantId && touched.tenantId && (
                        <ErrorMessage name="tenantId">
                        {(msg) => (
                          <StyledInlineErrorMessage>{msg}</StyledInlineErrorMessage>
                        )}
                      </ErrorMessage>
                      )}
                    </Section>
                    : null
                }
                <CheckLabel>
                  <CheckField
                    type="checkbox"
                    id="terms"
                    name="acceptToS"
                    error={touched.acceptToS && errors.acceptToS}
                  />
                  By signing up, I acknowledge that my personal data will be processed in accordance with Zervicepoint's&nbsp;
                  <TermsAndConditions text=' Privacy Policy' link='https://zervicepoint.com/en/privacy-policy/'/>
                  ,&nbsp;<TermsAndConditions text=' Data Protection' link='https://zervicepoint.com/en/data-protection/'/>
                  &nbsp;and agree to Zervicepoint's&nbsp;
                  <TermsAndConditions text=' Terms and Conditions.' link='https://zervicepoint.com/en/terms-of-service/'/>
                </CheckLabel>
                <CheckLabel>
                <CheckField
                  type="checkbox"
                  id="newsletter"
                  name="acceptFollow" />
                  I want to receive communication, tips and tricks from Zervicepoint.
                </CheckLabel>
                <Submit type="submit" disabled={!isValid || isSubmitting}>
                  {isSubmitting ? `Checking...` : `Create my store`}
                </Submit>
                {serverErrors ? <StyledInlineErrorMessage>{serverErrors}</StyledInlineErrorMessage> : null}
              </Form>

            </React.Fragment>
          );
        }}
      </Formik>
    </PageWrapper>
  );
}
export default NewStoreForm;
